import {
    Button,
    Checkbox,
    Container,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import {ChangeEvent, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {ErrorDetailContainer} from "../../api/config";
import {AccountService, ClientClaimDto} from "../../api/identity";
import {UserService} from "../../api/psc";
import {PageHeader, PrivilegeAlert} from "../../components/exports";
import ErrorParser from "../../components/shared/ErrorParser";
import {UserAccessTypes} from "../../components/shared/PrivilegeAlert";
import {fixMobileNumber} from "../../utils/language";

export default function CreateUser() {
    const navigate = useNavigate();

    const [user, setUser] = useState<{ displayName: string, mobileNumber: string }>({
        displayName: "",
        mobileNumber: "",

    });
    const [availableClaims, setAvailableClaims] = useState<ClientClaimDto[]>(
        []
    );
    const [selectedClaims, setSelectedClaims] = useState<number[]>([]);
    const [error, setError] = useState<ErrorDetailContainer | null>(null);
    const [requestInProgress, setRequestInProgress] = useState(false);

    useEffect(() => {
        AccountService.listAvailableClaims().then((r) => {
            setAvailableClaims(r);
            setSelectedClaims(r.map((i) => i.id));
        });
    }, []);

    const handleDisplayNameChange = (
        elem: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const userToUpdate = {...user, displayName: elem.target.value};
        setUser(userToUpdate);
    };

    const handlePhoneNumberChange = (
        elem: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const userToUpdate = {
            ...user,
            mobileNumber: fixMobileNumber(elem.target.value),
        };
        setUser(userToUpdate);
    };

    const toggleClaim = (id: number) => {
        if (selectedClaims.includes(id)) {
            setSelectedClaims(selectedClaims.filter((sc) => sc !== id));
        } else {
            setSelectedClaims([...selectedClaims, id]);
        }
    };

    const updateUser = async () => {
        setRequestInProgress(true);

        setError(null);

        try {
            const canAddUser = await UserService.canAddUser("1");

            if (!canAddUser) {
                setError(
                    ErrorDetailContainer.fromString(
                        "امکان افزودن کاربر جدید وجود ندارد."
                    )
                );
                return;
            }

            await AccountService.addUser({
                displayName: user.displayName,
                mobileNumber: user.mobileNumber,
                claimIds: selectedClaims,
            });
            navigate("/users");
        } catch (e) {
            setError(e as ErrorDetailContainer);
        } finally {
            setRequestInProgress(false);
        }
    };

    return (
        <Container maxWidth="xs">
            <PageHeader title="افزودن کاربر"/>

            <PrivilegeAlert access={UserAccessTypes.ManageUsers}/>

            <Paper sx={{p: 2}}>
                <Stack spacing={2}>
                    <TextField
                        fullWidth
                        onChange={handleDisplayNameChange}
                        value={user.displayName}
                        label="نام نمایشی"
                        variant="outlined"
                    />

                    <TextField
                        fullWidth
                        onChange={handlePhoneNumberChange}
                        value={user.mobileNumber}
                        label="شماره موبایل"
                        variant="outlined"
                    />

                    {availableClaims.map((c) => {
                        return (
                            <Stack
                                direction="row"
                                alignItems="center"
                                key={c.id}
                            >
                                <Checkbox
                                    onChange={() => toggleClaim(c.id)}
                                    checked={selectedClaims.includes(c.id)}
                                />
                                <Typography>{c.displayName}</Typography>
                            </Stack>
                        );
                    })}

                    <ErrorParser error={error}/>

                    <Button
                        fullWidth
                        disabled={requestInProgress}
                        variant="contained"
                        onClick={updateUser}
                    >
                        افزودن کاربر
                    </Button>
                </Stack>
            </Paper>
        </Container>
    );
}
